import { Check, ChevronsUpDown, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { RenderMeta } from "@/lib/api-types";
import { cn } from "@/utils/cn";

type FilterValues = {
  search: string;
  pedModel: string;
  collection: string;
  kind: string;
  slotId: string;
  drawableLocalIndex: string;
};

type QueryUpdateOptions = {
  resetPage?: boolean;
  replace?: boolean;
  clearSelectedAssetPath?: boolean;
};

export default function RenderCatalogFilters({
  meta,
  values,
  activeFilterCount,
  collectionComboboxOpen,
  onCollectionComboboxOpenChange,
  onUpdateQuery,
  onReset,
}: {
  meta: RenderMeta;
  values: FilterValues;
  activeFilterCount: number;
  collectionComboboxOpen: boolean;
  onCollectionComboboxOpenChange: (open: boolean) => void;
  onUpdateQuery: (
    updates: Record<string, string | number | undefined>,
    options?: QueryUpdateOptions,
  ) => void;
  onReset: () => void;
}) {
  const selectedCollectionLabel = values.collection
    ? meta.collections.find((item) => item.name === values.collection)?.name ||
      values.collection
    : "";

  return (
    <Card className="border-border/80 bg-card/80 lg:sticky lg:top-20">
      <CardHeader className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle>Filters</CardTitle>
          <div className="rounded-full border border-border/80 px-2 py-1 text-xs text-muted-foreground">
            {activeFilterCount} active
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 lg:max-h-[calc(100vh-8.5rem)] lg:overflow-y-auto">
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Search
          </p>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={values.search}
              onChange={(event) =>
                onUpdateQuery(
                  { search: event.target.value },
                  { resetPage: true, clearSelectedAssetPath: true },
                )
              }
              placeholder="Asset, texture, slot..."
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Ped model
          </p>
          <select
            value={values.pedModel}
            onChange={(event) =>
              onUpdateQuery(
                { pedModel: event.target.value },
                { resetPage: true, clearSelectedAssetPath: true },
              )
            }
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">All ped models</option>
            {meta.pedModels.map((item) => (
              <option key={item.name} value={item.name}>
                {item.name} ({item.count})
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Collection
          </p>
          <Popover
            open={collectionComboboxOpen}
            onOpenChange={onCollectionComboboxOpenChange}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={collectionComboboxOpen}
                className="w-full justify-between font-normal"
              >
                <span className="truncate">
                  {selectedCollectionLabel || "All collections"}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Tìm collection..." />
                <CommandList>
                  <CommandEmpty>Không tìm thấy collection.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="all collections"
                      onSelect={() => {
                        onUpdateQuery(
                          { collection: undefined },
                          { resetPage: true, clearSelectedAssetPath: true },
                        );
                        onCollectionComboboxOpenChange(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          !values.collection ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span>All collections</span>
                    </CommandItem>
                    {meta.collections.map((item) => {
                      const optionValue = item.name || "base";
                      const selected = values.collection === item.name;
                      return (
                        <CommandItem
                          key={optionValue}
                          value={`${optionValue} ${item.count}`}
                          onSelect={() => {
                            onUpdateQuery(
                              { collection: item.name },
                              { resetPage: true, clearSelectedAssetPath: true },
                            );
                            onCollectionComboboxOpenChange(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selected ? "opacity-100" : "opacity-0",
                            )}
                          />
                          <span className="truncate">{optionValue}</span>
                          <span className="ml-auto pl-2 text-xs text-muted-foreground">
                            {item.count}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Kind
          </p>
          <select
            value={values.kind}
            onChange={(event) =>
              onUpdateQuery(
                { kind: event.target.value },
                { resetPage: true, clearSelectedAssetPath: true },
              )
            }
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">All kinds</option>
            <option value="component">Component</option>
            <option value="prop">Prop</option>
          </select>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Slot ID
            </p>
            <Input
              value={values.slotId}
              onChange={(event) =>
                onUpdateQuery(
                  { slotId: event.target.value },
                  { resetPage: true, clearSelectedAssetPath: true },
                )
              }
              placeholder="e.g. 8"
            />
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Drawable
            </p>
            <Input
              value={values.drawableLocalIndex}
              onChange={(event) =>
                onUpdateQuery(
                  { drawableLocalIndex: event.target.value },
                  { resetPage: true, clearSelectedAssetPath: true },
                )
              }
              placeholder="Drawable index"
            />
          </div>
        </div>

        <div className="rounded-2xl border border-border/80 bg-muted/20 p-3 text-sm text-muted-foreground">
          <p className="font-medium text-foreground">Catalog scope</p>
          <p className="mt-1">
            {meta.summary.pedModelCount.toLocaleString()} ped models • {meta.summary.collectionCount.toLocaleString()} collections • {meta.summary.sourceAssetCount.toLocaleString()} source assets
          </p>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={onReset}
        >
          Reset filters
        </Button>
      </CardContent>
    </Card>
  );
}
